import {
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output
} from '@angular/core';
import { PaymentIntent, StripeError } from '@stripe/stripe-js';
import { StripeService } from 'ngx-stripe';
import { throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { PaymentIntentDto, StripePaymentIntentResponse, FinishPaymentRequest } from 'src/app/api/models';
import { UserStripePaymentsApiService } from 'src/app/api/services';
import { PaymentInfo, CustomerPaymentMethod, PaymentIds, PaymentObject, CardConfirmResponse, PaymentMethodEnum } from 'src/shared/models/payment.model';
import { BookingHelperService } from 'src/shared/services/helpers/booking-helper.service';
import { CustomSpinnerService } from 'src/shared/services/helpers/custom-spinner.service';
import { PaymentsHelperService } from 'src/shared/services/helpers/payments-helper.service';
import { MspApiService } from 'src/shared/services/msp-api.service';
import { IonButton } from "@ionic/angular/standalone";
import { TranslatePipe } from '@ngx-translate/core';
import { NgIf } from '@angular/common';

@Component({
	selector: 'vl-pay-with-saved-card',
	templateUrl: './pay-with-saved-card.component.html',
	styleUrls: ['./pay-with-saved-card.component.scss'],
	imports: [IonButton, TranslatePipe, NgIf],
})
export class PayWithSavedCardComponent implements OnInit {
	@Input() paymentInfo: PaymentInfo;
	@Input() paymentType: { type: 'new-card' | 'saved-card', card: CustomerPaymentMethod }
	@Output() onSuccess: EventEmitter<any> = new EventEmitter<any>();
	@Output() onError: EventEmitter<any> = new EventEmitter<any>();
	@Output() back: EventEmitter<any> = new EventEmitter<any>();

	paymentActive = false;
	isReady = false;
	payEnabled = false;
	paymentIds: PaymentIds;


	constructor(private stripeService: StripeService,
				private spinner: CustomSpinnerService,
				private stripePaymentService: UserStripePaymentsApiService,
				private bookingHelper: BookingHelperService,
				private paymentHelper: PaymentsHelperService,
				private mspApiService: MspApiService) {
	}

	ngOnInit() {
	}

	private createPaymentIntent() {
		console.log(this.paymentInfo.type);
		if (this.paymentInfo.type === PaymentObject.PayPerUse) {
			return this.stripePaymentService.createBookingPaymentIntent({
				body: {
					method: this.paymentType.card.type,
					bookingId: this.paymentInfo.bookingId,
					freeHours: this.paymentInfo.freeHoursUsed
				}
			});
		} else if (this.paymentInfo.type === PaymentObject.Subscription) {
			return this.stripePaymentService.createSubscriptionPaymentIntent({
				body: {
					method: this.paymentType.card.type,
					towerGroupId: this.paymentInfo.groupId,
					onDateTime: new Date(this.paymentInfo.subscriptionStartDate).toISOString()
				}
			});
		} else if (this.paymentInfo.type === PaymentObject.Purchase) {
			console.log(this.paymentInfo);
			return this.mspApiService.createPurchasePaymentIntent(this.paymentInfo?.purchase?.id, PaymentMethodEnum.STRIPE_CARD);
		}
	}

	onSubmit() {
		this.doPayment();
	}

	doPayment() {
		this.spinner.show(false).then(() => {
			this.bookingHelper.towerReadyToAction(this.paymentInfo.type, (valid) => {
				if(valid) {
				this.createPaymentIntent()
					.pipe(
						switchMap((pi) => {
							this.handlePaymentIds(pi);
							this.paymentActive = true;
							return this.getConfirmMethod(pi);
						}),
						catchError((e) => {
							return this.handlePaymentError(e);
						})
					).subscribe((result: CardConfirmResponse | PaymentIntentDto) => {

						switch (this.paymentType.card.type) {
							case PaymentMethodEnum.STRIPE_CARD:
								result = result as {
									paymentIntent?: PaymentIntent;
									error?: StripeError
								};
								if (result.error) {
									// The payment failed -- ask your customer for a new payment method.
									this.onError.emit();
									this.spinner.hide();
									this.paymentActive = false;
								} else {
									// The payment has succeeded.
									this.paymentActive = false;
									this.handlePaymentSuccess()
								}
								break;
						}
					}
				);
			}
			}, this.paymentInfo.bookingId, this.paymentInfo.groupId, this.paymentInfo.subscriptionStartDate);
		})
	}

	private handlePaymentError(e) {
		console.log('error', e);
		this.spinner.hide();
		if ((e.error === 'Internal Server Error' && e.path === '/api/user/payment/subscription') || e.status === 400) {
			this.onError.emit();
		}
		return throwError(e)
	}

	private handlePaymentIds(pi: StripePaymentIntentResponse) {
		this.paymentIds = {
			main: null,
			freeHours: null
		}
		Object.keys(pi.paymentsToMethod).forEach((k: any) => {
			console.log(k, pi.paymentsToMethod[k]);
			if (pi.paymentsToMethod[k] === 'FREE_HOURS') {
				this.paymentIds.freeHours = Number(k);
			} else {
				this.paymentIds.main = Number(k);
			}
		});
	}


	private getConfirmMethod(pi: StripePaymentIntentResponse) {
		let paymentParams = {};
		paymentParams = this.handleParams(paymentParams);
		return this.stripeService.confirmCardPayment(
			pi.paymentIntent.clientSecret,
			paymentParams,
			{ handleActions: true })
	}

	private handleParams(paymentParams: {}) {
		return Object.assign(paymentParams, {
			payment_method: this.paymentType.card.id
		});
	}

	handlePaymentSuccess() {
		let body: FinishPaymentRequest = {
			paymentId: this.paymentIds.main
		};

		if (this.paymentIds.freeHours) {
			body = {
				...body,
				...{ freeHoursPaymentId: this.paymentIds.freeHours }
			}
		}
		this.stripePaymentService.finishPayment({
			body: body
		}).subscribe(() => {
			this.analyticsCompletePayment();
			switch (this.paymentInfo.type) {
				case PaymentObject.PayPerUse:
					this.terminateBooking();
					break;
				case PaymentObject.Subscription:
					this.createSubscription(this.paymentIds.main, this.paymentInfo.subscriptionStartDate);
					break;
				case PaymentObject.Purchase:
					this.finishPurchase();
					break;
			}
		});
	}

	private analyticsCompletePayment() {
		const dataForAnalytics = { ...this.paymentInfo };
		dataForAnalytics.method = PaymentMethodEnum.STRIPE_CARD;
		// this.analyticsService.logEvent(AnalyticsEvents.CompletePayment, dataForAnalytics);
	}

	terminateBooking() {
		this.paymentHelper.terminateBooking(
			Number(this.paymentInfo.bookingId),
			this.paymentInfo.booking
		).subscribe(() => {
			this.paymentActive = false;
			this.onSuccess.emit()
			this.spinner.hide();
		})
	}

	createSubscription(paymentId, startDate) {
		this.paymentHelper.createSubscription(
			paymentId,
			this.paymentInfo.groupId,
			startDate
		).subscribe(() => {
			this.paymentActive = false;
			this.onSuccess.emit()
			this.spinner.hide();
		});
	}

	finishPurchase() {
		this.mspApiService.pingPayment(this.paymentInfo.purchase.id).subscribe(async () => {
			this.paymentActive = false;
			// await this.storageService.setItem(StorageEnums.PurchaseRedirectUrl, null);
			this.onSuccess.emit()
			this.spinner.hide();
		}, error => {
			this.paymentActive = false;
			this.onSuccess.emit()
			this.spinner.hide();
		});
	}
}
